// src/pages/Reports/Reportdelivery.js
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileAlt, faSpinner, faTimesCircle,
  faFilePdf, faCheckCircle, faTimes
} from '@fortawesome/free-solid-svg-icons';
import html2pdf from 'html2pdf.js';

import BASE_URL from '../../api';

const API_URL = `${BASE_URL}/api/report/reportdelivery`;
const CUSTOMER_API_URL = `${BASE_URL}/api/master/customer`;

const Reportdelivery = () => {
  const [form, setForm] = useState({ startdate: '', enddate: '', customerid: '' });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCust, setLoadingCust] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ delivered: [], pending: [] });
  const printRef = useRef();

  const token = () => localStorage.getItem('token');

  /* FETCH CUSTOMERS */
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await fetch(CUSTOMER_API_URL, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        const json = await res.json();
        if (json.success) setCustomers(json.data || []);
      } catch (e) { console.error(e); }
      setLoadingCust(false);
    };
    fetchCustomers();
  }, []);

  /* FETCH DELIVERY REPORT */
  const fetchReport = async () => {
    const { startdate, enddate, customerid } = form;
    if (!startdate || !enddate || !customerid) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    setError(null);
    setData({ delivered: [], pending: [] }); // Reset safely

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startdate, enddate, customerid }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to fetch report');

      if (json.success) {
        const delivered = Array.isArray(json.data?.delivered) ? json.data.delivered : [];
        const pending = Array.isArray(json.data?.pending) ? json.data.pending : [];
        setData({ delivered, pending });
      } else {
        setError(json.message);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /* HELPERS */
  const formatDate = d => d ? new Date(d).toLocaleDateString('en-IN') : '-';

  /* PDF GENERATION */
  const generatePDF = () => {
    const element = printRef.current;
    const customer = customers.find(c => c._id === form.customerid);
    const customerName = customer?.companyName || customer?.name || 'Unknown';

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Delivery_Report_${form.startdate}_to_${form.enddate}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get('pdf')
      .then(pdf => {
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.text(`Delivery Report | ${customerName} | ${formatDate(form.startdate)} - ${formatDate(form.enddate)}`, 0.6, 0.4);
          pdf.text(`Page ${i} of ${totalPages}`, 7.5, 0.4);
        }
      })
      .save();
  };

  // Safe access
  const delivered = Array.isArray(data.delivered) ? data.delivered : [];
  const pending = Array.isArray(data.pending) ? data.pending : [];
  const hasData = delivered.length + pending.length > 0;

  const customer = customers.find(c => c._id === form.customerid);
  const customerName = customer?.companyName || customer?.name || 'Unknown';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">

        {/* HEADER + PDF */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
            Delivery Report
          </h1>
          {hasData && (
            <button
              onClick={generatePDF}
              className="inline-flex items-center gap-2 px-5 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 shadow-lg transition"
            >
              <FontAwesomeIcon icon={faFilePdf} /> Download PDF
            </button>
          )}
        </div>

        {/* ERROR */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
            <FontAwesomeIcon icon={faTimesCircle} /> {error}
          </div>
        )}

        {/* FILTER */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <input
              type="date"
              value={form.startdate}
              onChange={e => setForm({ ...form, startdate: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none"
            />
            <input
              type="date"
              value={form.enddate}
              onChange={e => setForm({ ...form, enddate: e.target.value })}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 outline-none"
            />
            <select
              value={form.customerid}
              onChange={e => setForm({ ...form, customerid: e.target.value })}
              disabled={loadingCust}
              className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-400 bg-white outline-none"
            >
              <option value="">Select Customer</option>
              {customers.map(c => (
                <option key={c._id} value={c._id}>{c.companyName || c.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg transition-all"
          >
            {loading ? (
              <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Generating...</>
            ) : (
              <><FontAwesomeIcon icon={faSearch} /> Generate Report</>
            )}
          </button>
        </div>

        {/* PDF CONTENT (hidden) */}
        <div style={{ display: 'none' }}>
          <div ref={printRef} style={{ padding: '20px', fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>

            {/* DELIVERED */}
            {delivered.length > 0 && (
              <div style={{ marginBottom: '40px' }}>
                <h1 style={{ textAlign: 'center', margin: '0 0 10px', fontSize: '18px', fontWeight: 'bold' }}>Delivered Bookings</h1>
                <p style={{ textAlign: 'center', color: '#555', marginBottom: '20px' }}>
                  {customerName} | {formatDate(form.startdate)} to {formatDate(form.enddate)}
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#d1fae5' }}>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Docket</th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Consignee</th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Delivered On</th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {delivered.map(b => (
                      <tr key={b._id}>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.docketNumber}</td>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.consigneeid?.companyName || b.consigneeid?.name || '-'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{formatDate(b.deliveryDate)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>
                          Delivered
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* PENDING (NEW PAGE) */}
            {pending.length > 0 && (
              <div style={{ pageBreakBefore: 'always', marginTop: '40px' }}>
                <h1 style={{ textAlign: 'center', margin: '0 0 10px', fontSize: '18px', fontWeight: 'bold' }}>Pending Deliveries</h1>
                <p style={{ textAlign: 'center', color: '#555', marginBottom: '20px' }}>
                  {customerName} | {formatDate(form.startdate)} to {formatDate(form.enddate)}
                </p>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fee2e2' }}>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Docket</th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Consignee</th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Booked On</th>
                      <th style={{ border: '1px solid #ddd', padding: '6px', fontSize: '11px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map(b => (
                      <tr key={b._id}>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.docketNumber}</td>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{b.consigneeid?.companyName || b.consigneeid?.name || '-'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>{formatDate(b.bookingDate)}</td>
                        <td style={{ border: '1px solid #ddd', padding: '5px', fontSize: '10px' }}>
                          Pending
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ON-SCREEN CONTENT */}
        <div className="space-y-12">

          {/* DELIVERED */}
          {delivered.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                Delivered Bookings
              </h2>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Docket</th>
                      <th className="px-4 py-3 text-left">Consignee</th>
                      <th className="px-4 py-3 text-left">Delivered On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {delivered.map(b => (
                      <tr key={b._id} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-3">{b.docketNumber}</td>
                        <td className="px-4 py-3">{b.consigneeid?.companyName || b.consigneeid?.name || '-'}</td>
                        <td className="px-4 py-3">{formatDate(b.deliveryDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* PENDING */}
          {pending.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faTimes} className="text-red-600" />
                Pending Deliveries
              </h2>
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Docket</th>
                      <th className="px-4 py-3 text-left">Consignee</th>
                      <th className="px-4 py-3 text-left">Booked On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map(b => (
                      <tr key={b._id} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-3">{b.docketNumber}</td>
                        <td className="px-4 py-3">{b.consigneeid?.companyName || b.consigneeid?.name || '-'}</td>
                        <td className="px-4 py-3">{formatDate(b.bookingDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* NO DATA */}
          {!loading && !hasData && !error && (
            <div className="text-center py-12 text-gray-500">
              <p>No delivery data found for the selected date range and customer.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reportdelivery;